
import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Calendar, User, DollarSign, BookOpen } from "lucide-react";
import StudentLayout from "@/components/StudentLayout";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import { createApiUrl } from "@/lib/api";

interface ProfileData {
  id: string;
  name: string;
  email: string;
  phone: string;
  class_id: string;
  enrollment_date: string;
}

interface WeeklySummary {
  week_start: string;
  week_end: string;
  scheduled_classes: number;
  attended: number;
  remaining: number;
}

interface FeeRecord {
  id: string;
  amount: number;
  amount_paid: number;
  amount_due: number;
  due_date: string;
  payment_date: string | null;
  payment_status: string;
  cycle_type: string;
}

interface AttendanceRecord {
  id: string;
  date: string;
  status: string;
  teacher_name: string;
  class_type: string;
  marked_at: string;
}

const StudentDashboard = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [profileData, setProfileData] = useState<ProfileData | null>(null);
  const [weeklySummary, setWeeklySummary] = useState<WeeklySummary | null>(null);
  const [feesData, setFeesData] = useState<FeeRecord[]>([]);
  const [recentAttendance, setRecentAttendance] = useState<AttendanceRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const accessToken = localStorage.getItem('accessToken');
      if (!accessToken) {
        navigate('/login');
        return;
      }

      // Fetch all required data
      const [profileRes, weeklyRes, feesRes, attendanceRes] = await Promise.all([
        fetch(createApiUrl('/student/profile'), {
          headers: { 'Authorization': `Bearer ${accessToken}`, 'accept': 'application/json' }
        }),
        fetch(createApiUrl('/student/attendance-summary/week'), {
          headers: { 'Authorization': `Bearer ${accessToken}`, 'accept': 'application/json' }
        }),
        fetch(createApiUrl('/student/fees'), {
          headers: { 'Authorization': `Bearer ${accessToken}`, 'accept': 'application/json' }
        }),
        fetch(createApiUrl('/student/attendance-history'), {
          headers: { 'Authorization': `Bearer ${accessToken}`, 'accept': 'application/json' }
        })
      ]);

      if (profileRes.ok) setProfileData(await profileRes.json());
      if (weeklyRes.ok) setWeeklySummary(await weeklyRes.json());
      if (feesRes.ok) setFeesData(await feesRes.json());
      if (attendanceRes.ok) {
        const attendanceData = await attendanceRes.json();
        setRecentAttendance(attendanceData.slice(0, 3)); // Get last 3 records
      }

      if (profileRes.status === 401 || weeklyRes.status === 401 || feesRes.status === 401 || attendanceRes.status === 401) {
        localStorage.removeItem('studentData');
        localStorage.removeItem('accessToken');
        toast({
          title: "Session Expired",
          description: "Please login again",
          variant: "destructive"
        });
        navigate('/login');
      }
    } catch (error) {
      console.error('Dashboard fetch error:', error);
      toast({
        title: "Error",
        description: "Failed to load dashboard data",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <StudentLayout>
        <div className="flex items-center justify-center min-h-[300px] sm:min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-6 w-6 sm:h-8 sm:w-8 border-b-2 border-indigo-600 mx-auto mb-4"></div>
            <p className="text-gray-600 text-sm sm:text-base">Loading dashboard...</p>
          </div>
        </div>
      </StudentLayout>
    );
  }

  const calculateFeeTotals = () => {
    const totalAmount = feesData.reduce((sum, fee) => sum + fee.amount, 0);
    const totalPaid = feesData.reduce((sum, fee) => sum + fee.amount_paid, 0);
    const totalDue = feesData.reduce((sum, fee) => sum + fee.amount_due, 0);
    const paymentPercentage = totalAmount > 0 ? Math.round((totalPaid / totalAmount) * 100) : 0;
    return { totalAmount, totalPaid, totalDue, paymentPercentage };
  };

  const feeTotals = calculateFeeTotals();
  const weeklyAttendanceRate = weeklySummary 
    ? Math.round((weeklySummary.attended / weeklySummary.scheduled_classes) * 100) || 0
    : 0;

  return (
    <StudentLayout>
      <div className="space-y-4 sm:space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
          <div className="w-10 h-10 sm:w-12 sm:h-12 bg-indigo-100 rounded-full flex items-center justify-center flex-shrink-0">
            <User className="w-5 h-5 sm:w-6 sm:h-6 text-indigo-600" />
          </div>
          <div className="min-w-0 flex-1">
            <h1 className="text-xl sm:text-2xl font-bold text-gray-800 break-words">
              Welcome back, {profileData?.name || 'Student'}
            </h1>
            <p className="text-gray-600 text-sm sm:text-base">Here's an overview of your attendance and fees.</p>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Weekly Attendance</CardTitle>
              <Calendar className="h-4 w-4 text-muted-foreground flex-shrink-0" />
            </CardHeader>
            <CardContent>
              <div className="text-xl sm:text-2xl font-bold text-green-600">{weeklyAttendanceRate}%</div>
              <p className="text-xs text-muted-foreground">
                {weeklySummary?.attended || 0}/{weeklySummary?.scheduled_classes || 0} classes attended
              </p>
              <div className="mt-2">
                <Progress value={weeklyAttendanceRate} className="h-2" />
                <p className="text-xs sm:text-sm text-gray-600 mt-1">Your weekly attendance rate</p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Fee Status</CardTitle>
              <DollarSign className="h-4 w-4 text-muted-foreground flex-shrink-0" />
            </CardHeader>
            <CardContent>
              <div className="text-xl sm:text-2xl font-bold text-orange-600">₹{feeTotals.totalDue.toLocaleString()}</div>
              <p className="text-xs text-muted-foreground break-words">
                Paid: ₹{feeTotals.totalPaid.toLocaleString()} of ₹{feeTotals.totalAmount.toLocaleString()}
              </p>
              <div className="mt-2">
                <Progress value={feeTotals.paymentPercentage} className="h-2" />
                <p className="text-xs sm:text-sm text-gray-600 mt-1">Your current fee status</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Weekly Summary */}
        {weeklySummary && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base sm:text-lg">This Week's Classes</CardTitle>
              <p className="text-xs sm:text-sm text-gray-600 break-words">
                Week of {new Date(weeklySummary.week_start).toLocaleDateString()} - {new Date(weeklySummary.week_end).toLocaleDateString()}
              </p>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-2 sm:gap-4 text-center">
                <div>
                  <div className="text-xl sm:text-2xl font-bold text-blue-600">{weeklySummary.scheduled_classes}</div>
                  <p className="text-xs sm:text-sm text-gray-600">Scheduled</p>
                </div>
                <div>
                  <div className="text-xl sm:text-2xl font-bold text-green-600">{weeklySummary.attended}</div>
                  <p className="text-xs sm:text-sm text-gray-600">Attended</p>
                </div>
                <div>
                  <div className="text-xl sm:text-2xl font-bold text-orange-600">{weeklySummary.remaining}</div>
                  <p className="text-xs sm:text-sm text-gray-600">Remaining</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Recent Activities */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base sm:text-lg">Recent Attendance</CardTitle>
              <p className="text-xs sm:text-sm text-gray-600">Your latest attendance records</p>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {recentAttendance.length > 0 ? (
                  recentAttendance.map((record) => (
                    <div key={record.id} className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="min-w-0 flex-1 pr-3">
                        <p className="font-medium text-sm sm:text-base truncate">{record.teacher_name}</p>
                        <p className="text-xs sm:text-sm text-gray-600 truncate">
                          {new Date(record.date).toLocaleDateString()} - {record.class_type}
                        </p>
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs flex-shrink-0 ${
                        record.status.toLowerCase() === 'present'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {record.status}
                      </span>
                    </div>
                  ))
                ) : (
                  <p className="text-gray-500 text-center py-4 text-sm">No recent attendance records</p>
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base sm:text-lg">Recent Fees</CardTitle>
              <p className="text-xs sm:text-sm text-gray-600">Your recent fee records</p>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {feesData.slice(0, 3).map((fee) => (
                  <div key={fee.id} className="flex items-center justify-between p-3 border rounded-lg">
                    <div className="min-w-0 flex-1 pr-3">
                      <p className="font-medium text-sm sm:text-base">₹{fee.amount.toLocaleString()}</p>
                      <p className="text-xs sm:text-sm text-gray-600 truncate">{fee.cycle_type} fee</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        fee.payment_status === 'paid'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {fee.payment_status}
                      </span>
                      <p className="text-xs text-gray-500 mt-1">
                        Due: {new Date(fee.due_date).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                ))}
                {feesData.length === 0 && (
                  <p className="text-gray-500 text-center py-4 text-sm">No fee records available</p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </StudentLayout>
  );
};

export default StudentDashboard;
